import { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import type { WorkOrder, WorkOrderTask, WorkOrderNote, User } from '@shared/types'
import { CardGridSkeleton } from '../components/ui/Skeletons'
import { StatusBadge } from '../components/ui/StatusBadge'
import { GlowCard } from '../components/ui/GlowCard'
import { Breadcrumb } from '../components/ui/Breadcrumb'
import { EntityHeader } from '../components/ui/EntityHeader'
import { Button } from '../components/ui/Button'
import { Select } from '../components/ui/Select'

const PRIORITY_COLORS: Record<string, string> = {
  urgent: 'var(--err)',
  high: 'var(--accent)',
  normal: 'var(--info)',
  low: 'var(--text-3)',
}

const STATUS_OPTIONS = ['new', 'scheduled', 'dispatched', 'in_progress', 'on_hold', 'completed', 'cancelled']

function statusVariant(status: string): 'ok' | 'warn' | 'info' | 'default' | 'err' {
  const map: Record<string, 'ok' | 'warn' | 'info' | 'default' | 'err'> = {
    completed: 'ok', in_progress: 'info', dispatched: 'info', scheduled: 'warn',
    on_hold: 'warn', new: 'default', cancelled: 'err',
  }
  return map[status] || 'default'
}

function priorityVariant(priority: string): 'err' | 'warn' | 'info' | 'default' {
  const map: Record<string, 'err' | 'warn' | 'info' | 'default'> = {
    urgent: 'err', high: 'warn', normal: 'info', low: 'default',
  }
  return map[priority] || 'default'
}

function TruckIcon({ size = 20, style }: { size?: number; style?: React.CSSProperties }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={style}><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M15 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 13.52 9H14"/><circle cx="17" cy="18" r="2"/><circle cx="7" cy="18" r="2"/></svg>
}
function Building2Icon({ size = 14, style }: { size?: number; style?: React.CSSProperties }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={style}><path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/><path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"/><path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"/></svg>
}
function CalendarIcon({ size = 14, style }: { size?: number; style?: React.CSSProperties }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={style}><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
}
function UserIcon({ size = 14, style }: { size?: number; style?: React.CSSProperties }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={style}><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
}
function ClockIcon({ size = 14, style }: { size?: number; style?: React.CSSProperties }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={style}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
}

function formatDateTime(dt: string | null) {
  if (!dt) return ''
  try {
    return new Date(dt).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
    })
  } catch { return dt }
}

function formatTime(t: string | null) {
  if (!t) return ''
  try {
    return new Date(t).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
  } catch { return t }
}

interface WODetail extends WorkOrder {
  tasks: WorkOrderTask[]
  notes: WorkOrderNote[]
}

export default function WorkOrderDetail() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const [wo, setWo] = useState<WODetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [users, setUsers] = useState<User[]>([])

  // Edit states
  const [editingStatus, setEditingStatus] = useState(false)
  const [newNote, setNewNote] = useState('')
  const [addingNote, setAddingNote] = useState(false)
  const [newTaskDesc, setNewTaskDesc] = useState('')
  const [addingTask, setAddingTask] = useState(false)
  const [completing, setCompleting] = useState(false)
  const [resolution, setResolution] = useState('')
  const [showComplete, setShowComplete] = useState(false)

  const load = useCallback(async () => {
    if (!slug) return
    setLoading(true)
    try {
      const [data, userList] = await Promise.all([
        api.get<WODetail>(`/work-orders/${slug}`),
        api.get<User[]>('/users').catch(() => []),
      ])
      // Guard nested arrays in case API returns them as null/undefined
      if (data) {
        data.tasks = Array.isArray(data.tasks) ? data.tasks : []
        data.notes = Array.isArray(data.notes) ? data.notes : []
      }
      setWo(data)
      setUsers(Array.isArray(userList) ? userList : [])
    } catch { /* handled */ }
    finally { setLoading(false) }
  }, [slug])

  useEffect(() => { load() }, [load])

  async function handleStatusChange(status: string) {
    if (!wo) return
    try {
      await api.patch(`/work-orders/${slug}`, { status })
      load()
    } catch { /* */ }
    setEditingStatus(false)
  }

  async function handleAssignChange(userId: string) {
    if (!wo) return
    await api.patch(`/work-orders/${slug}`, { assigned_to: userId || null })
    load()
  }

  async function handleToggleTask(task: WorkOrderTask) {
    await api.patch(`/work-orders/${slug}/tasks/${task.id}`, {
      is_completed: task.is_completed ? 0 : 1,
    })
    load()
  }

  async function handleAddTask(e: React.FormEvent) {
    e.preventDefault()
    if (!newTaskDesc.trim()) return
    setAddingTask(true)
    try {
      await api.post(`/work-orders/${slug}/tasks`, {
        description: newTaskDesc.trim(),
        sort_order: (wo?.tasks?.length ?? 0),
      })
      setNewTaskDesc('')
      load()
    } catch { /* */ }
    finally { setAddingTask(false) }
  }

  async function handleDeleteTask(taskId: string) {
    await api.delete(`/work-orders/${slug}/tasks/${taskId}`)
    load()
  }

  async function handleAddNote(e: React.FormEvent) {
    e.preventDefault()
    if (!newNote.trim()) return
    setAddingNote(true)
    try {
      await api.post(`/work-orders/${slug}/notes`, {
        type: 'note',
        content: newNote.trim(),
      })
      setNewNote('')
      load()
    } catch { /* */ }
    finally { setAddingNote(false) }
  }

  async function handleStartWork() {
    await api.post(`/work-orders/${slug}/start`)
    load()
  }

  async function handleComplete() {
    setCompleting(true)
    try {
      await api.post(`/work-orders/${slug}/complete`, { resolution: resolution || undefined })
      setShowComplete(false)
      setResolution('')
      load()
    } catch { /* */ }
    finally { setCompleting(false) }
  }

  if (loading) return <CardGridSkeleton cards={3} />
  if (!wo) return <div style={{ padding: 24, color: 'var(--text-2)' }}>Work order not found.</div>

  const location = [wo.site_address, wo.site_city, wo.site_state].filter(Boolean).join(', ')
  const isActive = !['completed', 'cancelled'].includes(wo.status)
  const durationMinutes = wo.actual_start
    ? Math.round((new Date(wo.actual_end || new Date().toISOString()).getTime() - new Date(wo.actual_start).getTime()) / 60000)
    : null

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', animation: 'in-up 400ms var(--ease) both' }}>
      <Breadcrumb items={[
        { label: 'Dispatch', to: '/dispatch' },
        { label: wo.title },
      ]} />

      <EntityHeader
        icon={TruckIcon}
        title={wo.title}
        badges={[
          { label: wo.status.replace('_', ' '), variant: statusVariant(wo.status), dot: isActive },
          { label: wo.priority, variant: priorityVariant(wo.priority) },
          { label: wo.type, variant: 'default' as const },
        ]}
        metrics={[
          ...(wo.client_name ? [{ icon: Building2Icon, label: wo.client_name, value: '' as const }] : []),
          ...(wo.scheduled_date ? [{ icon: CalendarIcon, label: 'Scheduled', value: new Date(wo.scheduled_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) }] : []),
          ...(wo.assigned_name ? [{ icon: UserIcon, label: '', value: wo.assigned_name }] : []),
          ...(durationMinutes !== null ? [{ icon: ClockIcon, label: '', value: `${Math.floor(durationMinutes / 60)}h ${durationMinutes % 60}m` }] : []),
        ]}
        actions={[
          ...(isActive && !wo.actual_start ? [{ label: 'Start Work', onClick: handleStartWork, variant: 'primary' as const }] : []),
          ...(isActive && wo.actual_start ? [{ label: 'Complete', onClick: () => setShowComplete(true), variant: 'primary' as const }] : []),
        ]}
      />

      {/* Status & Assignment Row */}
      <GlowCard
        style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, padding: 16, marginBottom: 24 }}
        delay={100}
      >
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Status</label>
          {editingStatus ? (
            <Select
              options={STATUS_OPTIONS.map(s => ({ value: s, label: s.replace('_', ' ') }))}
              value={wo.status}
              onChange={(e) => handleStatusChange(e.target.value)}
              style={{ fontSize: 13 }}
            />
          ) : (
            <button
              onClick={() => setEditingStatus(true)}
              style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
            >
              <StatusBadge status={wo.status} />
              {isActive && <span style={{ fontSize: 10, color: 'var(--text-3)' }}>&#9660;</span>}
            </button>
          )}
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Assigned To</label>
          <Select
            options={[{ value: '', label: 'Unassigned' }, ...users.map(u => ({ value: u.id, label: u.full_name }))]}
            value={wo.assigned_to || ''}
            onChange={(e) => handleAssignChange(e.target.value)}
            style={{ fontSize: 13 }}
          />
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Scheduled</label>
          <div style={{ fontSize: 13, color: 'var(--text-1)', fontFamily: 'var(--font-mono)' }}>
            {wo.scheduled_date ? (
              <>
                {new Date(wo.scheduled_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                {wo.scheduled_start && ` ${formatTime(wo.scheduled_start)}`}
                {wo.scheduled_end && ` - ${formatTime(wo.scheduled_end)}`}
              </>
            ) : (
              <span style={{ color: 'var(--text-3)' }}>Not scheduled</span>
            )}
          </div>
        </div>
      </GlowCard>

      {/* Description */}
      {wo.description && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
            Description
          </h3>
          <GlowCard
            style={{ padding: 12, fontSize: 14, color: 'var(--text-2)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}
            delay={180}
          >
            {wo.description}
          </GlowCard>
        </div>
      )}

      {/* Linked Site */}
      {wo.site_name && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
            Linked Site
          </h3>
          <GlowCard style={{ padding: 12 }} delay={260}>
            <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--amber)' }}>{wo.site_name}</div>
            {location && <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>{location}</div>}
          </GlowCard>
        </div>
      )}

      {/* Checklist */}
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
          Checklist
        </h3>
        <GlowCard style={{ padding: 12 }} delay={340}>
          {(Array.isArray(wo.tasks) ? wo.tasks : []).length === 0 && !isActive ? (
            <p style={{ fontSize: 13, color: 'var(--text-3)' }}>No checklist items.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(Array.isArray(wo.tasks) ? wo.tasks : []).map(task => (
                <div key={task.id} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <input
                    type="checkbox"
                    checked={!!task.is_completed}
                    onChange={() => handleToggleTask(task)}
                    disabled={!isActive}
                    style={{ accentColor: 'var(--amber)', width: 16, height: 16 }}
                  />
                  <span style={{
                    fontSize: 14, color: task.is_completed ? 'var(--text-3)' : 'var(--text-1)',
                    textDecoration: task.is_completed ? 'line-through' : 'none',
                    flex: 1,
                  }}>
                    {task.description}
                  </span>
                  {isActive && (
                    <button
                      onClick={() => handleDeleteTask(task.id)}
                      style={{
                        fontSize: 12, color: 'var(--text-3)', padding: '2px 6px',
                        background: 'none', border: 'none', cursor: 'pointer',
                        transition: 'color var(--dur-f) var(--ease)',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.color = 'var(--err)' }}
                      onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-3)' }}
                    >
                      &times;
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
          {isActive && (
            <form onSubmit={handleAddTask} style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
              <input
                type="text"
                value={newTaskDesc}
                onChange={(e) => setNewTaskDesc(e.target.value)}
                className="v3-input"
                placeholder="Add checklist item..."
                style={{ fontSize: 13, flex: 1 }}
              />
              <Button type="submit" disabled={addingTask || !newTaskDesc.trim()} size="sm">
                Add
              </Button>
            </form>
          )}
        </GlowCard>
      </div>

      {/* Activity / Notes */}
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
          Activity
        </h3>
        <GlowCard style={{ padding: 12 }} delay={420}>
          {(Array.isArray(wo.notes) ? wo.notes : []).length === 0 ? (
            <p style={{ fontSize: 13, color: 'var(--text-3)' }}>No activity yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {(Array.isArray(wo.notes) ? wo.notes : []).map(note => (
                <div key={note.id} style={{ borderBottom: '1px solid var(--border)', paddingBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-3)', marginBottom: 2 }}>
                    <span style={{ fontWeight: 600, color: 'var(--text-2)' }}>{note.created_by_name || 'System'}</span>
                    <span>&middot;</span>
                    <span style={{ fontFamily: 'var(--font-mono)' }}>{formatDateTime(note.created_at)}</span>
                    {note.type !== 'note' && (
                      <>
                        <span>&middot;</span>
                        <span style={{ textTransform: 'capitalize' }}>{note.type.replace('_', ' ')}</span>
                      </>
                    )}
                  </div>
                  {note.content && (
                    <div style={{ fontSize: 14, color: 'var(--text-1)', whiteSpace: 'pre-wrap' }}>
                      {note.content}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {isActive && (
            <form onSubmit={handleAddNote} style={{ marginTop: 12, borderTop: (wo.notes ?? []).length ? '1px solid var(--border)' : 'none', paddingTop: (wo.notes ?? []).length ? 10 : 0 }}>
              <textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                className="v3-textarea"
                rows={2}
                placeholder="Add a note..."
                style={{ fontSize: 13, width: '100%', marginBottom: 8 }}
              />
              <Button type="submit" disabled={addingNote || !newNote.trim()} size="sm" loading={addingNote}>
                Add Note
              </Button>
            </form>
          )}
        </GlowCard>
      </div>

      {/* Time Logged */}
      {wo.actual_start && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
            Time Logged
          </h3>
          <GlowCard style={{ padding: 12, fontSize: 14, color: 'var(--text-1)', fontFamily: 'var(--font-mono)' }} delay={500}>
            <span>{wo.assigned_name || 'Tech'}: {formatTime(wo.actual_start)}</span>
            {wo.actual_end ? (
              <span> - {formatTime(wo.actual_end)}</span>
            ) : (
              <span> - (in progress)</span>
            )}
            {durationMinutes != null && (
              <span style={{ marginLeft: 8, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>
                &middot; {(durationMinutes / 60).toFixed(1)} hrs
              </span>
            )}
          </GlowCard>
        </div>
      )}

      {/* Resolution */}
      {wo.resolution && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
            Resolution
          </h3>
          <GlowCard style={{ padding: 12, fontSize: 14, color: 'var(--text-1)', whiteSpace: 'pre-wrap' }} delay={580}>
            {wo.resolution}
          </GlowCard>
        </div>
      )}

      {/* Action Buttons */}
      {isActive && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 32 }}>
          {wo.status !== 'in_progress' && wo.status !== 'completed' && (
            <Button onClick={handleStartWork}>
              Start Work
            </Button>
          )}
          {wo.status === 'in_progress' && !showComplete && (
            <Button variant="success" onClick={() => setShowComplete(true)}>
              Complete Work Order
            </Button>
          )}
          {showComplete && (
            <div style={{ flex: 1, padding: 16, backgroundColor: 'var(--bg-1)', borderRadius: 10, border: '1px solid var(--border)' }}>
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-2)', display: 'block', marginBottom: 4 }}>Resolution / Summary</label>
              <textarea
                value={resolution}
                onChange={(e) => setResolution(e.target.value)}
                className="v3-textarea"
                rows={3}
                placeholder="What was done..."
                style={{ width: '100%', marginBottom: 12 }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Button onClick={handleComplete} loading={completing}>
                  Complete
                </Button>
                <Button variant="ghost" onClick={() => setShowComplete(false)}>Cancel</Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
